import React from 'react';
import { connect } from 'react-redux';
import moment from 'moment';
import { withToastManager } from 'react-toast-notifications';
import ReactTooltip from 'react-tooltip';

import DayPickerRange from './day-picker-range';
import LayerTooltip from '../leftpanel/leftpanel-components/layer-components/layer-tooltip';

import MapController from '../../controllers/MapController';
import TimelineController from '../../controllers/TimelineController';

import {
  ITimelineProps,
  ITimelineState,
  ITimelineDispatchProps,
  IDateRangeObject,
  IImageryLayer,
  IImagePair
} from '../../types/timeline';

import config from '../../config/config';
import { RootState } from '../../reducers/app';
import { clearCurrentImagePair, setTimelineOpen } from '../../reducers/auditdata/actions';
import { setTimelineDates } from '../../reducers/leftpanel/actions';

import { ISentinelLayerResult, ILandsatLayerResult } from '../../types/layers';

import 'rc-slider/assets/index.css';
import './timeline.scss';
import InfoSvg from '../../assets/info-icon';

type Props = ReturnType<typeof mapStateToProps> &
  ITimelineProps &
  ITimelineDispatchProps &
  ReturnType<typeof withToastManager>;

const initialState = {
  maxDateRange: TimelineController.initialDateRange,
  dateRange: {
    startDate: moment().subtract(1, 'year'),
    endDate: moment(),
  },
  focusedDateInput: null,
  cloudCover: config.timeline.cloudCover,
  visibleImageLayers: ['landsat', 'sentinel'],
  disabledInput: '',
  highlightedImage: null,
  hasBeforeImage: false,
};

/**
 * Component for displaying,and interacting with, a Timeline of imagery data
 */
class TimeLine extends React.Component<Props, ITimelineState> {
  private timelineChartRef = React.createRef<HTMLDivElement>();

  constructor(props: Props) {
    super(props);
    this.state = { ...initialState };
  }

  componentDidUpdate(prevProps: Props) {
    const { visibleImageLayers } = this.state;
    const {
      open,
      landsatResults,
      sentinelResults,
      currentImagePair,
      imagePairs,
      clearCurrentSelectedPair,
    } = this.props;

    if (prevProps.open !== open) {
      if (open) {
        if (landsatResults || sentinelResults) {
          // When we open the timeline and have data, we want to instantiate the Image Chart if we've not already done so otherwise, we want to make sure it's updated with the latest date range / cloud cover
          if (!TimelineController._imageChart) {
            TimelineController.initializeTimeline(
              this.timelineChartRef,
              visibleImageLayers,
              landsatResults,
              sentinelResults,
              this.state.cloudCover,
              this.props.timelineDateRange
            );
          } else {
            TimelineController.resetChartDrillDown();
            TimelineController.updateTimelineData({ ...this.state.dateRange, cloudCover: this.state.cloudCover });
          }
        }
      } else {
        clearCurrentSelectedPair();
        if (imagePairs.length < 1) {
          MapController.clearImagesFromMap();
        } else {
          this.addImagePairImagesToMap();
        }
      }
      TimelineController.updateTimelineAndBoundingBoxes();
    }

    if (prevProps.imagePairs.length !== imagePairs.length) {
      TimelineController.updateTimelineAndBoundingBoxes();
      if (imagePairs.length > 0 && (!currentImagePair.before && !currentImagePair.after)) {
        this.addImagePairImagesToMap();
      }
    }

    if (
      prevProps.currentImagePair.before !== currentImagePair.before ||
      prevProps.currentImagePair.after !== currentImagePair.after
    ) {
      // When we click an image on the timeline, we want to add it to the map, while also removing previously clicked ones
      if (currentImagePair) {
        Object.keys(currentImagePair).forEach((key) => {
          if ((key === 'before' || key === 'after') && currentImagePair[key]) {
            MapController.clearImagesFromMap();
            if (currentImagePair.before && currentImagePair.before.locked && !currentImagePair.after) {
              TimelineController.updateTimelineAndBoundingBoxes();
            }
            if (currentImagePair[key].imageLayer === IImageryLayer.landsat) {
              const image = landsatResults?.find(
                (res: ILandsatLayerResult) => res.acquisitiondate === currentImagePair[key].acquisitionDate
              );
              if (image) {
                MapController.addImagesToMap('landsat', [image.objectid]);
              }
            } else if (currentImagePair[key].imageLayer === IImageryLayer.sentinel) {
              const image = sentinelResults?.find(
                (res: ISentinelLayerResult) => res.acquisitiondate === currentImagePair[key].acquisitionDate && res.tilename === currentImagePair[key].highlightID
              );
              if (image) {
                MapController.addImagesToMap('sentinel', [image.objectid]);
              }
            }
          }
        });
      }
    }
  }

  addImagePairImagesToMap = () => {
    const imageIds: Array<number> = [];
    this.props.imagePairs.forEach((pair:IImagePair) => {
      imageIds.push(pair.after.oid) // We only want to see After images on the map
    });
    MapController.addImagesToMap(IImageryLayer[this.props.imagePairs[0].before.imageLayer], imageIds);
  };

  /**
   * Filter the timeline by cloud cover
   * @param cloudCover - cloud cover value passed from Select input
   */
  updateCloudCover = (value: number) => {
    this.setState(
      {
        ...this.state,
        cloudCover: value,
      },
      () => {
        const updateData = {
          cloudCover: value
        };
        TimelineController.updateTimelineData(updateData);
      }
    );
  };

  /**
   * Filter timeline by date range
   * @param dates - date range object passed from DateRangePicker
   */
  updateDateRange = (dates: IDateRangeObject) => {
    const { startDate, endDate } = dates;
    if (moment(endDate).isSameOrBefore(startDate)) {
      dates = {
        startDate: endDate,
        endDate: startDate,
      };
    }
    this.setState({
      dateRange: dates,
    });
    TimelineController.updateTimelineDates(dates, false);
    this.props._setTimelineDates(dates);
    const updateData = {
      cloudCover: this.state.cloudCover,
      startDate: startDate,
      endDate: endDate,
    };
    TimelineController.updateTimelineData(updateData);
  };

  /**
   * Function to set the currently selected image for analysis
   */
  setSelectedImage = () => {
    const { landsatResults, sentinelResults, currentImagePair, toastManager, timelineDateRange } = this.props;
    if (currentImagePair) {
      TimelineController._imageChart?.drillUp(); // We need to drillUp here so we have access to our original series again
      if (currentImagePair.isBefore) {
        const alert = `Selecting the last data point as your 'Before' image is not allowed.`;
        if (IImageryLayer[currentImagePair.before?.imageLayer] === 'landsat' && landsatResults) {
          if (currentImagePair.before.acquisitionDate === landsatResults[landsatResults.length - 1].acquisitiondate) {
            toastManager.add(alert, { appearance: 'error', autoDismiss: true });
            return;
          }
        }

        if (IImageryLayer[currentImagePair.before?.imageLayer] === 'sentinel' && sentinelResults) {
          if (currentImagePair.before.acquisitionDate === sentinelResults[sentinelResults.length - 1].acquisitiondate) {
            toastManager.add(alert, { appearance: 'error', autoDismiss: true });
            return;
          }
        }
      }
      const { before, after } = currentImagePair;
      let dates;
      if (before && before.locked && after) {
        // We don't need after to be locked, as this is fired when a user locks in the after image
        dates = {
          ...timelineDateRange // reset dates for next selection of image pairs
        }
      } else if (before && !before.locked) {
        dates = { 
          startDate: moment(before.acquisitionDate), // set start date so user can't pick after image earlier than before image
          endDate: TimelineController._dateRange.endDate
        };
      }
      if (dates) {
        TimelineController.updateTimelineDates(dates, true);
      }

      TimelineController.saveSelectedImage();
    }
  };

  hasSelectedAnImage = () => {
    const { currentImagePair } = this.props;
    if (
      (currentImagePair.isBefore && currentImagePair.before) ||
      (!currentImagePair.isBefore && currentImagePair.after)
    ) {
      return true;
    } else {
      return false;
    }
  };

  render() {
    const { open, closeTimeline, currentImagePair, mapLoaderActive } = this.props;
    const { before } = currentImagePair;
    const { maxDateRange, dateRange, cloudCover } = this.state;

    return (
      <div className={`timeline-container ${open ? 'open' : ''}`}>
        <div className='timeline-header'>
          <div className='timeline-title-container'>
            <p className='title'>Land Cover Image Timeline</p>
            <LayerTooltip id='tooltip-timeline' tip={config.helpertext.timelineInfo}>
              <InfoSvg />
            </LayerTooltip>
            <ReactTooltip
              className='tooltip timeline'
              id='tooltip-timeline'
              place='right'
              type='success'
              effect='solid'
              clickable={true}
            />
          </div>
          <div className='timeline-controls'>
            <div className='timeline-controls__filters'>
              <div className='timeline-controls__datepicker'>
                <p>Select Dates</p>
                <div className='datepicker-wrapper'>
                  <DayPickerRange
                    dateRange={dateRange}
                    maxDateRange={maxDateRange}
                    updateDateRange={this.updateDateRange}
                  />
                </div>
              </div>
              <div className='timeline-controls__cloudcover'>
                <p>Cloud Cover</p>
                <select
                  id='cloudCover'
                  name='cloudCover'
                  defaultValue={config.timeline.cloudCoverOptions.find((cc) => cloudCover === cc) || config.timeline.cloudCover}
                  className='cloudcover-dropdown'
                  onChange={(e) => this.updateCloudCover(Number(e.target.value))}
                >
                  {config.timeline.cloudCoverOptions.map((cc) => (
                    <option key={cc} value={cc}>{`${cc > 0 ? '<' : ''} ${cc}%`}</option>
                  ))}
                </select>
              </div>
              <div className='timeline-controls__actions-set-image'>
                <p>Set As</p>
                <button
                  disabled={mapLoaderActive || !this.hasSelectedAnImage()}
                  className='timeline-controls__set-image-button'
                  onClick={() => this.setSelectedImage()}
                >
                  {!before || (before && !before.locked) ? 'Before Image' : 'After Image'}
                </button>
              </div>
            </div>
          </div>
          <button className='timeline-header__close-button esri-icon-close' onClick={() => closeTimeline()} />
        </div>
        <div className='timeline-body' ref={this.timelineChartRef} />
      </div>
    );
  }
}

const mapDispatchToProps = {
  closeTimeline: () => setTimelineOpen(false),
  _setTimelineDates: (dates: IDateRangeObject) => setTimelineDates(dates),
  clearCurrentSelectedPair: () => clearCurrentImagePair(),
};

const mapStateToProps = (state: RootState) => ({
  currentImagePair: state.auditdata.currentImagePair,
  imagePairs: state.auditdata.selectedImagePairs.imagePairs,
  timelineDateRange: state.leftpanel.timelineDateRange,
  mapLoaderActive: state.mapview.loader,
});

export default withToastManager(connect(mapStateToProps, mapDispatchToProps)(TimeLine));
